// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: link previews (the card the unfurler builds for a pasted link, as opposed to a bot's rich
// embed) all draw as one card of one width, with the picture full width under the text. A YouTube
// link plays inside that card, even when the unfurler only had the page's title and thumbnail.

import {hasRichEmbedContent} from '@app/features/channel/components/embeds/EmbedRenderUtils';
import {EMBED_MAX_WIDTH} from '@app/features/messaging/utils/MediaDimensionConfig';
import {MessageEmbedTypes} from '@konvo/constants/src/ChannelConstants';
import type {MessageEmbed} from '@konvo/schema/src/domains/message/EmbedSchemas';

/** The media width plus the card's padding (0.875rem each side) and hairline border. */
export const LINK_PREVIEW_WIDTH = EMBED_MAX_WIDTH + 30;
/** A thumbnail at least this wide is a picture worth showing large; smaller ones are logos and stay beside the text. */
const LARGE_THUMBNAIL_MIN_WIDTH = 320;
const YOUTUBE_HOSTS = new Set([
	'youtube.com',
	'www.youtube.com',
	'm.youtube.com',
	'music.youtube.com',
	'youtube-nocookie.com',
	'www.youtube-nocookie.com',
	'youtu.be',
]);
const YOUTUBE_ID = /^[A-Za-z0-9_-]{6,15}$/;
const YOUTUBE_TITLE_SUFFIX = /\s+-\s+YouTube$/;
const PLAYER_WIDTH = 1280;
const PLAYER_HEIGHT = 720;

export function isLinkPreview(embed: Pick<MessageEmbed, 'type' | 'url'>): boolean {
	return embed.type !== MessageEmbedTypes.RICH && Boolean(embed.url);
}

/**
 * A page preview that sits inside the message's bubble: the unfurler's card for a pasted page or a
 * YouTube video. Bots' rich embeds, Konvo's cards and bare picture or video links keep their own look.
 */
export function isBubbleLinkPreview(embed: MessageEmbed): boolean {
	if (!isLinkPreview(embed) || !hasRichEmbedContent(embed)) return false;
	if (embed.provider?.name?.startsWith('Konvo:')) return false;
	if (embed.type === MessageEmbedTypes.LINK || embed.type === MessageEmbedTypes.ARTICLE) return true;
	return embed.type === MessageEmbedTypes.VIDEO && parseYouTubeVideo(embed.url) !== null;
}

/** The site a preview links to, as people read it: "developer.apple.com", without "www.". */
export function linkPreviewDomain(url: string | undefined): string | null {
	if (!url) return null;
	try {
		return new URL(url).hostname.replace(/^www\./, '') || null;
	} catch {
		return null;
	}
}

export function hasLargeThumbnail(embed: Pick<MessageEmbed, 'thumbnail'>): boolean {
	return (embed.thumbnail?.width ?? 0) >= LARGE_THUMBNAIL_MIN_WIDTH;
}

function parseTimestamp(value: string | null): number | null {
	if (!value) return null;
	const match = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s?)?$/.exec(value);
	if (!match || !match[0]) return null;
	const seconds = Number(match[1] ?? 0) * 3600 + Number(match[2] ?? 0) * 60 + Number(match[3] ?? 0);
	return seconds > 0 ? seconds : null;
}

/** The video id and start time of a YouTube video link (watch, youtu.be, shorts, live, embed). */
export function parseYouTubeVideo(url: string | undefined): {id: string; start: number | null} | null {
	if (!url) return null;
	let parsed: URL;
	try {
		parsed = new URL(url);
	} catch {
		return null;
	}
	const host = parsed.hostname.toLowerCase();
	if (!YOUTUBE_HOSTS.has(host)) return null;
	const segments = parsed.pathname.split('/').filter(Boolean);
	let id: string | null | undefined;
	if (host === 'youtu.be') {
		id = segments[0];
	} else if (segments[0] === 'watch') {
		id = parsed.searchParams.get('v');
	} else if (['shorts', 'live', 'embed', 'v'].includes(segments[0] ?? '')) {
		id = segments[1];
	}
	if (!id || !YOUTUBE_ID.test(id)) return null;
	return {id, start: parseTimestamp(parsed.searchParams.get('t') ?? parsed.searchParams.get('start'))};
}

/**
 * The embed with a playable YouTube player, or null when it is not a YouTube video. Without a
 * YouTube API key the unfurler stores a plain link with the page's thumbnail; the player is built
 * from the link so those (and older messages) play in the card too.
 */
export function youTubePlayerEmbed(embed: MessageEmbed): MessageEmbed | null {
	if (!embed.thumbnail?.proxy_url) return null;
	const video = parseYouTubeVideo(embed.url);
	if (!video) return null;
	if (embed.video?.url && parseYouTubeVideo(embed.video.url)?.id === video.id) return embed;
	const player = new URL(`https://www.youtube.com/embed/${video.id}`);
	if (video.start) player.searchParams.set('start', String(video.start));
	return {
		...embed,
		title: cleanLinkTitle(embed.title),
		video: {url: player.toString(), width: PLAYER_WIDTH, height: PLAYER_HEIGHT, flags: 0},
	};
}

/** Drops the " - YouTube" a page title carries when the unfurler read it from the HTML. */
export function cleanLinkTitle(title: string | undefined): string | undefined {
	return title?.replace(YOUTUBE_TITLE_SUFFIX, '');
}
